import {
    ButtonGroup,
    Container,
    Flex,
    Heading,
    HStack,
    Td,
    Tr,
    IconButton,
    Box,
    Badge,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { Form, Table } from "components";
import { Moment } from "config";
import { useEffect, useState } from "react";
import usePurchaseSaleDetails from "../../../hooks/usePurchaseSaleDetailsList";

export default function PurchaseSaleDetails() {
    const initialPeriod = [Moment().subtract(3, "months").toDate(), Moment().toDate()];

    const [period, setPeriod] = useState(initialPeriod);
    const [orderBy, setOrderBy] = useState("total_sales");
    const [type, setType] = useState(2);
    const [page, setPage] = useState(1);

    const validPeriod =
        period && period[0] instanceof Date && period[1] instanceof Date
            ? period
            : null;

    const purchaseDetails = usePurchaseSaleDetails({
        page,
        order_by: orderBy,
        period: validPeriod,
        type,
    });

    useEffect(() => {
        setPage(1);
    }, [orderBy, type, period]);

    const handlePeriodChange = (value) => setPeriod(value);

    const formatCurrency = (value) => {
        if (!value && value !== 0) return "R$ 0,00";
        return new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
            minimumFractionDigits: 2,
        }).format(value);
    };

    const { meta } = purchaseDetails.data || {};

    return (
        <Container maxW="95%" mt={10}>
            <Flex justifyContent="space-between" alignItems="center">
                <Heading>Relatório - Detalhamento de Compras e Vendas</Heading>
                <ButtonGroup size="xs">
                    <IconButton
                        icon={<RepeatIcon />}
                        aria-label="Recarregar"
                        isLoading={purchaseDetails.isFetching}
                        onClick={purchaseDetails.refetch}
                    />
                </ButtonGroup>
            </Flex>

            {/* Filtros */}
            <HStack mt={4} spacing={2}>
                <Form.Select
                    fontSize="sm"
                    p={0}
                    m={0}
                    onChange={(e) => setType(parseInt(e.target.value, 10))}
                    placeholder="Tipo"
                    options={[
                        { label: "Produtos", value: 2 },
                        { label: "Voucher", value: 4 },
                    ]}
                />
                <Form.Select
                    fontSize="sm"
                    p={0}
                    m={0}
                    onChange={(e) => setOrderBy(e.target.value)}
                    placeholder="Ordenar por"
                    options={[
                        { label: "Data de aprovação", value: "approved_at" },
                        { label: "Data do pedido", value: "created_at" },
                        { label: "Total Compras", value: "total_purchases" },
                        { label: "Total Vendas", value: "total_sales" },
                    ]}
                />
                <Form.Interval initial={initialPeriod} onSelect={handlePeriodChange} />
            </HStack>

            {/* ALERTA INFORMATIVO */}
            <Alert
                status="info"
                variant="subtle"
                mt={4}
                borderRadius="md"
                alignItems="start"
                backgroundColor="blue.50"
            >
                <AlertIcon boxSize="24px" color="blue.500" />
                <Box textAlign="left">
                    <AlertTitle fontWeight="bold" color="blue.700">
                        Importante!
                    </AlertTitle>
                    <AlertDescription fontSize="sm" color="blue.700">
                        É possível que os valores de vendas ou a quantidade de itens vendidos
                        sejam maiores do que os comprados. Isso ocorre porque o relatório soma
                        todas as transações aprovadas no período, podendo incluir descontos
                        nas compras e movimentação de estoque pré-existente.
                        <br />
                        <br />
                        <strong>Exemplo:</strong> um núcleo pode ter comprado{" "}
                        <strong>1.769 itens</strong> e vendido <strong>2.205</strong> no mesmo
                        período, pois utilizou parte do estoque de meses anteriores.
                    </AlertDescription>
                </Box>
            </Alert>

            {/* Tabela */}
            <Box mt={4}>
                {purchaseDetails.isLoading ? (
                    <Box borderRadius="md" overflow="hidden">
                        {[...Array(8)].map((_, row) => (
                            <Flex
                                key={row}
                                mb={2}
                                alignItems="center"
                                className="shimmer-row"
                                height="40px"
                            >
                                <Box flex="2" mr={2} borderRadius="md" className="shimmer" />
                                <Box flex="1" borderRadius="md" className="shimmer" />
                            </Flex>
                        ))}
                    </Box>
                ) : (
                    <Table
                        columns={[
                            { children: "Núcleo/Distribuidor" },
                            { children: "Total Compras", isNumeric: true },
                            { children: "Total Vendas", isNumeric: true },
                            { children: "Diferença (R$)", isNumeric: true },
                            { children: "% Vendas/Compras", isNumeric: true },
                            { children: "Itens Comprados", isNumeric: true },
                            { children: "Itens Vendidos", isNumeric: true },
                            { children: "Diferença Itens", isNumeric: true },
                        ]}
                        setPage={setPage}
                        meta={meta}
                    >
                        {purchaseDetails.data?.data?.map((item, index) => (
                            <Tr
                                key={item.id || index}
                                _hover={{ backgroundColor: "rgba(0,0,0,0.03)" }}
                            >
                                <Td>{item.name}</Td>
                                <Td isNumeric>{formatCurrency(item.total_purchases)}</Td>
                                <Td isNumeric>{formatCurrency(item.total_sales)}</Td>
                                <Td isNumeric>
                                    <Badge
                                        colorScheme={
                                            item.difference_values >= 0 ? "green" : "red"
                                        }
                                        minW="90px"
                                        textAlign="center"
                                        borderRadius="md"
                                        px={2}
                                        py={1}
                                    >
                                        {formatCurrency(item.difference_values)}
                                    </Badge>
                                </Td>
                                <Td isNumeric>
                                    {item.percentage_of_sales_over_purchases
                                        ? `${item.percentage_of_sales_over_purchases}%`
                                        : "-"}
                                </Td>
                                <Td isNumeric>{item.total_items_purchased}</Td>
                                <Td isNumeric>{item.total_items_sold}</Td>
                                <Td isNumeric>
                                    <Badge
                                        colorScheme={
                                            item.difference_items >= 0 ? "green" : "red"
                                        }
                                        minW="60px"
                                        textAlign="center"
                                        borderRadius="md"
                                        px={2}
                                        py={1}
                                    >
                                        {item.difference_items}
                                    </Badge>
                                </Td>
                            </Tr>
                        ))}
                    </Table>
                )}
            </Box>

            {/* Shimmer CSS */}
            <style jsx>{`
                .shimmer {
                    height: 100%;
                    background: linear-gradient(
                            90deg,
                            #f0f0f0 25%,
                            #b7b6b6 50%,
                            #f0f0f0 75%
                    );
                    background-size: 200% 100%;
                    animation: shimmer 1.5s infinite;
                }

                @keyframes shimmer {
                    0% {
                        background-position: -200% 0;
                    }
                    100% {
                        background-position: 200% 0;
                    }
                }

                .shimmer-row {
                    display: flex;
                    gap: 8px;
                }
            `}</style>
        </Container>
    );
}
